<template>
  <div class="product-reviews-block" ref="reviewContainer">
    <Divider margin-top="var(--space-10)" margin-bottom="var(--space-10)" />

    <ProductReviewSummary
      :loading="loading"
      :error="error"
      :filterStar="filters.rating"
      :filterTag="filters.tag"
      :availableTags="enableSimilar ? [] : availableTags"
      :reviewCount="countExactProduct || (product && product.review ? product.review.count : 0)"
      @updateStars="updateStarsFilter"
      @updateTags="updateTagsFilter"
    ></ProductReviewSummary>
    <hr />

    <template v-if="reviewsExactProduct && product">
      <ProductReview
        v-for="review in reviewsExactProduct"
        :key="review.id"
        :review="review"
        :product-title="product.title"
        :show-product="false"
        :show-featured="true"
      ></ProductReview>
    </template>

    <template v-if="(!countExactProduct || !count) && (!loading || reviews.length)">
      <div v-if="product && product.review && !product.review.count" class="no-reviews">
        <div>Hmm, there aren’t any reviews yet<template v-if="product"> for the {{ product.title }}</template> —</div>
        <div>which means you might get to write the first one. (Congrats!)</div>
      </div>
      <div v-else-if="error" class="no-reviews">
        <div>Hmm, something went wrong while loading reviews. Please try again later.</div>
      </div>
      <div v-else class="no-reviews">No reviews match the selected filters.</div>
    </template>

    <template v-if="product && reviewsSimilarProduct && reviewsSimilarProduct.length">
      <div class="similar-reviews-title">Reviews for similar products</div>
      <hr />
      <ProductReview
        v-for="review in reviewsSimilarProduct"
        :key="review.id"
        :review="review"
        :show-product="true"
        :show-featured="false"
      ></ProductReview>
    </template>
    <template v-if="enableLoadMore">
      <div v-if="loading" class="read-more-reviews-button"><div class="loader tiny"></div></div>
      <button v-else-if="!loading" class="read-more-reviews-button small outlined coral" @click.prevent="loadMore">
        Read More Reviews
      </button>
    </template>
  </div>
</template>

<script>
import { onServerPrefetch, onMounted } from '@vue/composition-api';
import { Divider } from '@article/pixel';

import ProductReview from './ProductReview.vue';
import ProductReviewSummary from './ProductReviewSummary.vue';

import useProductReviews from '@/compositions/useProductReviews.js';

export default {
  name: 'ProductReviews',
  components: {
    ProductReview,
    ProductReviewSummary,
    Divider,
  },
  props: {
    product: {
      type: Object,
      default: () => ({})
    }
  },

  computed: {
    reviewsExactProduct() {
      return (this.reviews || []).filter(review => this.isProductExactMatchReview(this.filters.product, review));
    },

    reviewsSimilarProduct() {
      if(this.enableSimilar) {
        return (this.reviews || []).filter(review => !this.isProductExactMatchReview(this.filters.product, review));
      }
      return [];
    },

    enableSimilar() {
      const SIMILAR_REVIEW_THRESHOLD = 5;
      return this.countExactProduct < SIMILAR_REVIEW_THRESHOLD;
    },

    enableLoadMore() {
      if(!this.isMore || !this.count) {
        return false;
      }

      const loadedCount = this.reviews.length;
      const loadedExactCount = this.reviewsExactProduct.length;

      if(this.enableSimilar) {
        return loadedCount < this.count;
      }
      return loadedExactCount < this.countExactProduct;
    }
  },

  setup(props, { refs, emit }) {
    const {
      filters,
      availableTags,
      count,
      countExactProduct,
      page,
      maxPage,
      reviews,
      loading,
      error,
      isMore,
      load,
      loadMore,
      updateTagsFilter,
      updateStarsFilter
    } = useProductReviews();

    let intersectionTimeout = null;

    function checkEngagement() {
      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.intersectionRatio > 0.5) {
                intersectionTimeout = setTimeout(() => {
                  emit('reviewsEngaged');
                  observer.unobserve(refs.reviewContainer);
                }, 5000);
              } else {
                clearTimeout(intersectionTimeout);
              }
            });
          },
          {
            threshold: 0.5,
          },
        );

        observer.observe(refs.reviewContainer);
      }
    }

    onServerPrefetch(async () => {
      await load(props?.product?.hashKey);
    });

    onMounted(async () => {
      await load(props?.product?.hashKey);
      checkEngagement();
    });

    return {
      filters,
      availableTags,
      count,
      countExactProduct,
      page,
      maxPage,
      reviews,
      loading,
      error,
      isMore,
      load,
      loadMore,
      updateTagsFilter,
      updateStarsFilter
    };
  },

  methods: {
    isProductExactMatchReview(hashKey, review) {
      return !![...(review.alternativeHashKeys || []), review.activeVersionHashKey, review.productHashKey].find(hk => hk === hashKey);
    }
  }
}
</script>

<style lang="scss">
@import '~@article/pixel/dist/style/variables';
@import '~@article/pixel/dist/style/mixins';

.product-reviews-block {
  .similar-reviews-title {
    font-size: var(--font-size-16);
    line-height: var(--line-height-16);
    font-family: var(--font-bold);
    margin-top: var(--space-6);
    margin-bottom: var(--space-2);
  }

  .read-more-reviews-button {
    margin-top: var(--space-6);
  }

  .no-reviews {
    text-align: center;
    margin-top: var(--space-10);
    margin-bottom: var(--space-10);
  }
}
</style>
